import { useContext, useEffect, useState, type FC } from "react";
import MainLayout from "../components/MainLayout";
import { Avatar, AvatarGroup, Button, Checkbox, IconButton } from "rsuite";
import { LoadingContext } from "../contexts/LoadingContext";
import { SearchContext } from "../contexts/SearchContext";
import {
  createBanner,
  deleteBanner,
  getBanners,
  updateBanner,
} from "../services/sales/BannerApi";
import { errorToast } from "../utils/helper-ui";
import { PaginationResponse } from "../objects/pagination";
import { getPagination } from "../utils/helper";
import { Banner } from "../models/sales/banner";
import { Table, Pagination } from "rsuite";
import PlusIcon from "@rsuite/icons/Plus";
import { BsTrash3 } from "react-icons/bs";
import BannerForm from "../components/features/sales/BannerForm";
import { FileModel } from "../models/file/file";
const { Column, HeaderCell, Cell } = Table;
interface BannerPageProps {}

const BannerPage: FC<BannerPageProps> = ({}) => {
  const { search, setSearch } = useContext(SearchContext);
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const [mounted, setMounted] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(30);
  const [pagination, setPagination] = useState<PaginationResponse>();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [modalFormOpen, setModalFormOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState<Banner>();

  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys.length === banners.length) {
    checked = true;
  } else if (checkedKeys.length === 0) {
    checked = false;
  } else if (checkedKeys.length > 0 && checkedKeys.length < banners.length) {
    indeterminate = true;
  }

  const handleCheckAll = (
    value: any,
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const keys = checked ? banners.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };
  const handleCheck = (
    value: any,
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const keys = checked
      ? [...checkedKeys, value as string]
      : checkedKeys.filter((item) => item !== (value as string));
    setCheckedKeys(keys);
  };

  useEffect(() => {
    setMounted(true);

    return () => {
      setMounted(false);
    };
  }, []);

  useEffect(() => {
    if (!mounted) return;
    fetchData();
  }, [mounted, search, page, size]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await getBanners({ page, size, search });
      const resJson = await res.json();
      //   console.log(data);
      setBanners((prev) => [...prev, ...resJson.data.items]);
      setPagination(getPagination(resJson.data));
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const removeBanner = async (banner: Banner) => {
    try {
      setIsLoading(true);
      if (
        window.confirm(
          `Are you sure you want to delete banner with name ${banner.title}?`
        )
      ) {
        await deleteBanner(banner.id!);
        fetchData();
      }
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };
  const saveBanner = async (banner: Banner) => {
    try {
      setIsLoading(true);
      if (selectedBanner) {
        await updateBanner(selectedBanner.id!, banner);
      } else {
        await createBanner(banner);
      }
      setModalFormOpen(false);
      setSelectedBanner(undefined);
      fetchData();
      setBanners([]);
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };
  const renderHeader = () => (
    <div className="flex flex-row justify-between items-center px-2">
      <h1 className="text-2xl font-semibold">Banner</h1>
      {/* button */}
      <div>
        <IconButton
          size="sm"
          appearance="primary"
          color="green"
          icon={<PlusIcon />}
          onClick={() => setModalFormOpen(true)}
        >
          Create Banner
        </IconButton>
      </div>
    </div>
  );
  return (
    <MainLayout header={renderHeader()}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left border bg-gray-50">
                <Checkbox
                  inline
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={handleCheckAll}
                />
              </th>
              <th className="px-4 py-2 text-left border bg-gray-50">Title</th>
              <th className="px-4 py-2 text-left border bg-gray-50">
                Description
              </th>
              <th className="px-4 py-2 text-left border bg-gray-50">Images</th>
              <th className="px-4 py-2 text-left border bg-gray-50">URL</th>
              <th className="px-4 py-2 text-left border bg-gray-50">Action</th>
            </tr>
          </thead>
          <tbody>
            {banners.map((rowData, i) => (
              <tr key={i}>
                <td className="px-4 py-2 border">
                  <Checkbox
                    value={rowData.id}
                    inline
                    onChange={handleCheck}
                    checked={checkedKeys.some((item) => item === rowData.id)}
                  />
                  {i + 1}
                </td>
                <td className="px-4 py-2 border">
                  <div
                    className="hover:underline cursor-pointer font-semibold "
                    onClick={() => {
                      setSelectedBanner(rowData as Banner);
                      setModalFormOpen(true);
                    }}
                  >
                    {rowData.title}
                  </div>
                </td>
                <td className="px-4 py-2 border">
                  <div
                    dangerouslySetInnerHTML={{ __html: rowData.description }}
                  ></div>
                </td>
                <td className="px-4 py-2 border">
                  <div className="flex flex-wrap">
                    <AvatarGroup stack>
                      {(rowData?.images ?? [])
                        .filter((p: FileModel, j: number) => j < 5)
                        .map((p: FileModel) => (
                          <Avatar
                            size="xs"
                            bordered
                            circle
                            key={p.id}
                            src={p.url}
                            alt={p.file_name}
                          />
                        ))}
                      {(rowData?.images ?? []).length - 5 > 0 && (
                        <Avatar
                          size="xs"
                          bordered
                          circle
                          style={{ background: "#111" }}
                        >
                          +{(rowData?.images ?? []).length - 5}
                        </Avatar>
                      )}
                    </AvatarGroup>
                  </div>
                </td>
                <td className="px-4 py-2 border">{rowData.url}</td>
                <td className="px-4 py-2 border">
                  <BsTrash3
                    className="text-red-500 hover:text-red-800"
                    size={16}
                    onClick={() => removeBanner(rowData as Banner)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!pagination?.last && (
        <div className="flex justify-center my-2">
          <Button
            onClick={() => {
              setPage((pagination?.page ?? 0) + 1);
            }}
          >
            Load more
          </Button>
        </div>
      )}
      {/* <div style={{ padding: 20 }}>
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="xs"
          layout={["total", "-", "limit", "|", "pager", "skip"]}
          total={pagination?.total ?? 0}
          limitOptions={[10, 30, 50]}
          limit={pagination?.size}
          activePage={pagination?.page}
          onChangePage={setPage}
          onChangeLimit={setSize}
        />
      </div> */}
      <BannerForm
        banner={selectedBanner}
        open={modalFormOpen}
        onClose={() => {
          setModalFormOpen(false);
          setSelectedBanner(undefined);
        }}
        onSubmit={saveBanner}
      />
    </MainLayout>
  );
};
export default BannerPage;
